import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { BuyerLeadEnquiryCardEnquiryFragment } from '@graphql/__generated__/graphql';
import {
  parseSuggestionsMap,
  toSuggestionLanguage,
} from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailFormDialog } from '@modules/_shared/components/email-form/EmailFormDialog';
import type { EmailFormRecipient } from '@modules/_shared/components/email-form/types';
import { getEnquiryResponseEmailTemplate } from '@modules/_shared/components/listing-marketing/enquiryResponseEmailTemplate';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useEnquiryResponseActions } from '@modules/enquiries/useEnquiryResponseActions';
import { EmailBody } from '@shared/components/EmailBody';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';
import { sanitizeEmailHtml } from '@shared/utils/sanitizeEmailHtml';

gql(/* GraphQL */ `
  fragment BuyerLeadEnquiryCardEnquiry on enquiries {
    id
    message
    raw_email
    created_at
    processed
    portal {
      id
      name
    }
    lot {
      id
      enquiry_response_email_templates: email_templates(
        where: { type: { _eq: "enquiry_auto_response" } }
        order_by: { updated_at: desc_nulls_last }
        limit: 1
      ) {
        id
        ...EmailTemplateSelect
      }
      development {
        id
        enquiry_response_override_lot
        email_templates(
          where: { type: { _eq: "enquiry_auto_response" } }
          order_by: { updated_at: desc_nulls_last }
          limit: 1
        ) {
          id
          ...EmailTemplateSelect
        }
      }
    }
    user {
      id
      full_name
      emails(order_by: [{ primary: desc }, { verified: desc }]) {
        id
        email
      }
    }
    response_activity {
      id
      email_message {
        id
        status
        date
      }
    }
  }
`);

const GET_BUYER_LEAD_ENQUIRY = gql(/* GraphQL */ `
  query GetBuyerLeadEnquiry($buyerLeadId: uuid!) {
    enquiries(
      where: { buyer_lead_id: { _eq: $buyerLeadId } }
      order_by: { created_at: desc }
      limit: 1
    ) {
      ...BuyerLeadEnquiryCardEnquiry
    }
  }
`);

const GET_BUYER_LEAD_ENQUIRY_BY_ID = gql(/* GraphQL */ `
  query GetBuyerLeadEnquiryById($buyerLeadId: uuid!, $enquiryId: uuid!) {
    enquiries(
      where: { id: { _eq: $enquiryId }, buyer_lead_id: { _eq: $buyerLeadId } }
      limit: 1
    ) {
      ...BuyerLeadEnquiryCardEnquiry
    }
  }
`);

const GET_BUYER_LEAD_ENQUIRY_RESPONSE_SUGGESTIONS = gql(/* GraphQL */ `
  query GetBuyerLeadEnquiryResponseSuggestions(
    $senderId: uuid
    $recipients: [uuid!]
    $enquiryId: uuid
    $language: GetEmailTemplateSuggestionsLanguage
  ) {
    get_email_template_suggestions(
      senderId: $senderId
      recipients: $recipients
      enquiryId: $enquiryId
      language: $language
    ) {
      suggestions
    }
  }
`);

type Enquiry = BuyerLeadEnquiryCardEnquiryFragment;

// `raw_email` is attacker-controllable HTML from inbound portal emails.
// EmailBody renders it inside a sandboxed iframe (sandbox="" — no scripts,
// no same-origin, no top navigation); `sanitizeEmailHtml` covers what the
// sandbox does not (a nested `<body style>` repainting the iframe, a link
// `target="_self"` navigating it), as for the other three EmailBody hosts.
// JSON-shaped payloads are not HTML and must not be rendered.
const getRawEmailHtml = (rawEmail: string | null): string | null => {
  if (rawEmail == null) {
    return null;
  }
  try {
    JSON.parse(rawEmail);
    return null;
  } catch {
    return rawEmail;
  }
};

const EnquiryBody: React.FC<{
  enquiry: Enquiry;
  rawEmailHtml: string | null;
}> = ({ enquiry, rawEmailHtml }) => {
  const { t } = useLocale();
  const hasMessage = enquiry.message != null && enquiry.message !== '';
  const [showOriginal, setShowOriginal] = useState<boolean>(!hasMessage);

  return (
    <Stack sx={{ gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        <TimeAgo dateString={enquiry.created_at} addSuffix />
      </Typography>
      {hasMessage && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {enquiry.message}
        </Typography>
      )}
      {rawEmailHtml != null && (
        <>
          <Box>
            <Button
              variant="text"
              startIcon={showOriginal ? <EyeOffIcon /> : <EyeIcon />}
              onClick={() => setShowOriginal(prev => !prev)}
            >
              {showOriginal
                ? t('Hide original email')
                : t('Show original email')}
            </Button>
          </Box>
          {showOriginal && <EmailBody html={sanitizeEmailHtml(rawEmailHtml)} />}
        </>
      )}
    </Stack>
  );
};

export const BuyerLeadEnquiryCard: React.FC<{
  buyerLeadId: string;
  enquiryId?: string | null;
  lotId: string | null;
  canEdit: boolean;
}> = ({ buyerLeadId, enquiryId, lotId, canEdit }) => {
  const { t, language } = useLocale();
  const { me } = useAppData();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [showResponseForm, setShowResponseForm] = useState(false);

  const { data: latestEnquiryData } = useQuery(GET_BUYER_LEAD_ENQUIRY, {
    variables: { buyerLeadId },
    skip: enquiryId != null,
  });
  const { data: selectedEnquiryData } = useQuery(GET_BUYER_LEAD_ENQUIRY_BY_ID, {
    variables: { buyerLeadId, enquiryId: enquiryId ?? '' },
    skip: enquiryId == null,
  });

  const enquiry =
    enquiryId != null
      ? selectedEnquiryData?.enquiries[0]
      : latestEnquiryData?.enquiries[0];
  const { data: suggestionsData, loading: suggestionsLoading } = useQuery(
    GET_BUYER_LEAD_ENQUIRY_RESPONSE_SUGGESTIONS,
    {
      variables: {
        senderId: me?.id,
        recipients: enquiry?.user?.id != null ? [enquiry.user.id] : [],
        enquiryId: enquiry?.id,
        language: toSuggestionLanguage(language),
      },
      skip: enquiry == null || me?.id == null,
    },
  );

  const {
    markProcessed,
    markingProcessed,
    handleEnquiryResponseSent,
    updatingResponse,
  } = useEnquiryResponseActions({
    enquiryId: enquiry?.id,
    buyerLeadId,
    onError: setErrorLike,
    onCompleted: emitFeedRefresh,
  });
  const rawEmailHtml = useMemo(
    () => getRawEmailHtml(enquiry?.raw_email ?? null),
    [enquiry?.raw_email],
  );

  const contactName = enquiry?.user?.full_name?.trim();
  const contactEmail = enquiry?.user?.emails[0]?.email;
  const responseTemplate =
    getEnquiryResponseEmailTemplate({
      development: enquiry?.lot?.development,
      listingTemplates: enquiry?.lot?.enquiry_response_email_templates,
    }) ?? null;
  const responseTemplateSuggestions = useMemo(
    () =>
      parseSuggestionsMap(
        suggestionsData?.get_email_template_suggestions?.suggestions,
      ),
    [suggestionsData],
  );

  const responseRecipients = useMemo<EmailFormRecipient[]>(() => {
    if (contactEmail == null || enquiry?.user?.id == null) {
      return [];
    }
    return [{ id: enquiry.user.id, email: contactEmail, name: contactName }];
  }, [contactEmail, contactName, enquiry?.user?.id]);

  if (enquiry == null) {
    return null;
  }

  const portalName = enquiry.portal?.name;
  const title =
    portalName != null ? `${t('Enquiry')} (${portalName})` : t('Enquiry');
  const hasContent = enquiry.message != null || rawEmailHtml != null;
  const hasResponse = enquiry.response_activity != null;
  const responseStatus = enquiry.response_activity?.email_message?.status;

  return (
    <>
      <ListingAccordion title={title} defaultExpanded={!enquiry.processed}>
        <Stack sx={{ gap: 1.5 }}>
          {hasContent && (
            <EnquiryBody enquiry={enquiry} rawEmailHtml={rawEmailHtml} />
          )}

          {hasResponse ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('Response sent')}{' '}
                <TimeAgo
                  dateString={enquiry.response_activity?.email_message?.date}
                  addSuffix
                />
              </Typography>
              {responseStatus === 'error' && (
                <Chip size="small" color="error" label={t('statusError')} />
              )}
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              {!enquiry.processed && (
                <Button
                  variant="outlined"
                  loading={markingProcessed}
                  disabled={!canEdit}
                  onClick={() => markProcessed()}
                >
                  {t('Mark as processed')}
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!canEdit || contactEmail == null}
                loading={updatingResponse || suggestionsLoading}
                onClick={() => setShowResponseForm(true)}
              >
                {t('Reply')}
              </Button>
              {enquiry.processed && (
                <Chip size="small" color="success" label={t('Processed')} />
              )}
            </Stack>
          )}
        </Stack>
      </ListingAccordion>

      <EmailFormDialog
        open={showResponseForm}
        parentGlobalId={toGlobalId('Enquiry', enquiry.id)}
        initialRecipients={responseRecipients}
        fallbackToRecipients={
          contactEmail == null
            ? []
            : [{ email: contactEmail, name: contactName }]
        }
        initialSubject={t('Enquiry response')}
        initialTemplate={responseTemplate}
        templateSuggestions={responseTemplateSuggestions}
        campaignMode={false}
        snippetLotId={enquiry.lot?.id ?? lotId}
        onClose={() => setShowResponseForm(false)}
        onEmailSent={handleEnquiryResponseSent}
      />

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
